import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { CheckCircle, Clock, Package, Truck, AlertCircle, Loader2, RefreshCw, Heart } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STEPS = [
  { key: 'received', label: 'Recibido', Icon: CheckCircle, color: '#25D366' },
  { key: 'in_process', label: 'En proceso', Icon: Clock, color: '#D4AF37' },
  { key: 'out_for_delivery', label: 'En entrega', Icon: Truck, color: '#0F2922' },
  { key: 'delivered', label: 'Entregado', Icon: Package, color: '#0F2922' },
];

const OrderTrackPage = () => {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get('token');

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menu, setMenu] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [choice, setChoice] = useState('refund'); // refund | replace
  const [replacementId, setReplacementId] = useState('');
  const [replacementOption, setReplacementOption] = useState('');
  const [replacementQty, setReplacementQty] = useState(1);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/orders/${id}/track`, { params: { token } });
      setOrder(res.data);
    } catch (e) {
      console.error(e);
      toast.error('No se encontró el pedido o el link no es válido.');
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    load();
    // Cargar menú para reemplazo
    axios.get(`${API}/menu`).then(r => setMenu(r.data)).catch(() => {});
  }, [load, token]);

  const submitChoice = async () => {
    if (choice === 'replace' && !replacementId) {
      toast.error('Elige un producto de reemplazo');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/orders/${id}/substitution`, {
        choice,
        replacement_item_id: choice === 'replace' ? replacementId : null,
        replacement_option: choice === 'replace' ? (replacementOption || null) : null,
        replacement_quantity: choice === 'replace' ? replacementQty : 1,
      }, { params: { token } });
      toast.success('¡Decisión enviada! Recibirás confirmación por email cuando se procese.');
      await load();
    } catch (e) {
      console.error(e);
      toast.error('Error al enviar tu decisión');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#B85042]" /></div>;
  }
  if (!token || !order) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] pt-24 px-4 flex items-center justify-center">
        <Card className="max-w-md bg-white rounded-2xl">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-[#B85042] mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-bold text-[#0F2922] mb-2">Pedido no encontrado</h2>
            <p className="text-[#5C6C62]">Verifica el link que recibiste por email.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentStatus = order.fulfillment_status || 'received';
  const isSubstitution = currentStatus === 'substitution_needed';
  const sub = order.substitution || {};
  const customerResponded = sub && sub.customer_choice && sub.customer_choice !== 'pending';
  const currentStepIndex = STATUS_STEPS.findIndex(s => s.key === currentStatus);

  const selectedReplacement = menu.find(m => m.id === replacementId);

  return (
    <div className="min-h-screen bg-[#FAF9F6] pt-24 pb-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl font-bold text-[#0F2922]" data-testid="track-title">Pedido #{order.id.slice(0,8)}</h1>
            <p className="text-[#5C6C62]">Hola {order.customer_name}, este es el estado de tu pedido</p>
          </div>
          <Button variant="outline" onClick={load} className="rounded-xl" data-testid="track-refresh">
            <RefreshCw className="w-4 h-4 mr-2" />Actualizar
          </Button>
        </div>

        {/* Status timeline */}
        <Card className="bg-white rounded-2xl mb-6" data-testid="status-timeline-card">
          <CardContent className="p-6">
            {isSubstitution ? (
              <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl p-5 flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-[#B85042] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-heading font-bold text-[#0F2922]">Requiere tu atención</h3>
                  <p className="text-[#5C6C62] text-sm mt-1">{sub.item_name && (<>Lamentamos: <b>{sub.item_name}</b> se agotó. </>)}{sub.admin_note}</p>
                </div>
              </div>
            ) : (
              <div className="flex justify-between items-start">
                {STATUS_STEPS.map((step, idx) => {
                  const reached = idx <= currentStepIndex;
                  const Icon = step.Icon;
                  return (
                    <div key={step.key} className="flex flex-col items-center flex-1" data-testid={`step-${step.key}`}>
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${reached ? 'bg-[#B85042] text-white shadow-lg' : 'bg-[#E5E3D8] text-[#5C6C62]'}`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <span className={`text-xs font-body mt-2 text-center ${reached ? 'text-[#0F2922] font-bold' : 'text-[#5C6C62]'}`}>{step.label}</span>
                      {idx < STATUS_STEPS.length - 1 && (
                        <div className={`absolute h-0.5 ${reached ? 'bg-[#B85042]' : 'bg-[#E5E3D8]'}`} style={{ width: 'calc(25% - 24px)', marginLeft: 'calc(12.5% + 36px)', marginTop: '24px' }} />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Substitution decision */}
        {isSubstitution && !customerResponded && (
          <Card className="bg-white rounded-2xl mb-6" data-testid="substitution-card">
            <CardHeader>
              <CardTitle className="font-heading text-[#0F2922]">¿Qué prefieres hacer?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer ${choice === 'refund' ? 'border-[#B85042] bg-[#B85042]/5' : 'border-[#E5E3D8]'}`}>
                <input type="radio" name="choice" value="refund" checked={choice === 'refund'} onChange={() => setChoice('refund')} data-testid="choice-refund" />
                <div className="flex-1">
                  <div className="flex items-center gap-2"><Heart className="w-4 h-4 text-[#B85042]" /><span className="font-body font-bold text-[#0F2922]">Reembolso de este item</span></div>
                  <p className="text-sm text-[#5C6C62] mt-1">Te devolvemos el monto de <b>{sub.item_name}</b> ({sub.quantity || 1} x ${(sub.item_price || 0).toFixed(2)} MXN). El resto del pedido sigue normal.</p>
                </div>
              </label>

              <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer ${choice === 'replace' ? 'border-[#B85042] bg-[#B85042]/5' : 'border-[#E5E3D8]'}`}>
                <input type="radio" name="choice" value="replace" checked={choice === 'replace'} onChange={() => setChoice('replace')} data-testid="choice-replace" />
                <div className="flex-1">
                  <div className="flex items-center gap-2"><Package className="w-4 h-4 text-[#0F2922]" /><span className="font-body font-bold text-[#0F2922]">Cambiar por otro producto</span></div>
                  <p className="text-sm text-[#5C6C62] mt-1">Elige cualquier otro producto del menú. Si la diferencia es a favor te la devolvemos; si es en contra, te enviamos un link para cobrar la diferencia.</p>
                </div>
              </label>

              {choice === 'replace' && (
                <div className="space-y-3 p-4 bg-[#FAF9F6] rounded-xl" data-testid="replace-form">
                  <div>
                    <label className="text-sm font-body text-[#0F2922] block mb-1">Producto de reemplazo</label>
                    <select value={replacementId} onChange={(e) => { setReplacementId(e.target.value); setReplacementOption(''); }} className="w-full h-10 px-3 border border-[#E5E3D8] rounded-xl bg-white text-sm" data-testid="replace-product">
                      <option value="">-- Selecciona --</option>
                      {menu.filter(m => m.available).map(m => (
                        <option key={m.id} value={m.id}>{m.name_es} — ${m.price.toFixed(2)} MXN</option>
                      ))}
                    </select>
                  </div>
                  {selectedReplacement && selectedReplacement.options && selectedReplacement.options.length > 0 && (
                    <div>
                      <label className="text-sm font-body text-[#0F2922] block mb-1">Opción</label>
                      <select value={replacementOption} onChange={(e) => setReplacementOption(e.target.value)} className="w-full h-10 px-3 border border-[#E5E3D8] rounded-xl bg-white text-sm" data-testid="replace-option">
                        <option value="">-- Selecciona --</option>
                        {selectedReplacement.options.map((opt, i) => (
                          <option key={i} value={opt.label_es}>{opt.label_es}{opt.extra_price > 0 ? ` (+$${opt.extra_price})` : ''}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-body text-[#0F2922] block mb-1">Cantidad</label>
                    <input type="number" min="1" value={replacementQty} onChange={(e) => setReplacementQty(parseInt(e.target.value) || 1)} className="w-full h-10 px-3 border border-[#E5E3D8] rounded-xl text-sm" data-testid="replace-qty" />
                  </div>
                </div>
              )}

              <Button onClick={submitChoice} disabled={submitting} className="w-full bg-[#B85042] hover:bg-[#B85042]/90 text-white rounded-xl py-6" data-testid="submit-choice">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enviando...</> : 'Confirmar mi decisión'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Decision already submitted */}
        {isSubstitution && customerResponded && (
          <Card className="bg-white rounded-2xl mb-6" data-testid="decision-sent-card">
            <CardContent className="p-6 text-center">
              <CheckCircle className="w-12 h-12 text-[#25D366] mx-auto mb-3" />
              <h3 className="font-heading text-xl font-bold text-[#0F2922]">¡Decisión recibida!</h3>
              <p className="text-[#5C6C62] mt-2">Estamos procesando tu solicitud: <b>{sub.customer_choice === 'refund' ? 'Reembolso del item' : `Cambio por ${sub.replacement_item_name_es || ''}`}</b>.</p>
              {sub.diff_action === 'charge_extra' && (
                <p className="text-sm text-[#B85042] mt-2">Diferencia a cobrar: ${(sub.diff_mxn || 0).toFixed(2)} MXN. Te contactaremos pronto.</p>
              )}
              {sub.diff_action === 'refund_difference' && (
                <p className="text-sm text-[#25D366] mt-2">Te devolveremos: ${Math.abs(sub.diff_mxn || 0).toFixed(2)} MXN.</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Order summary */}
        <Card className="bg-white rounded-2xl" data-testid="order-summary-card">
          <CardHeader><CardTitle className="font-heading text-[#0F2922]">Detalle del pedido</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {(order.items || []).map((it, i) => (
              <div key={i} className={`flex justify-between text-sm py-2 ${sub.affected_item_index === i ? 'bg-[#D4AF37]/10 px-2 rounded-lg' : ''}`}>
                <span className="font-body text-[#0F2922]">{it.name_es || it.name}{it.selected_option && <span className="text-[#B85042]"> · {it.selected_option}</span>} x{it.quantity}</span>
                <span className="font-body text-[#5C6C62]">${(it.subtotal || 0).toFixed(2)} MXN</span>
              </div>
            ))}
            <div className="border-t border-[#E5E3D8] pt-2 mt-2">
              <div className="flex justify-between text-sm"><span>Subtotal</span><span>${(order.subtotal || 0).toFixed(2)} MXN</span></div>
              <div className="flex justify-between text-sm"><span>Envío</span><span>{order.is_pickup ? 'Recoger en tienda' : `$${(order.delivery_cost || 0).toFixed(2)} MXN`}</span></div>
              <div className="flex justify-between font-bold text-[#0F2922] pt-1"><span>Total</span><span>${(order.total_amount || 0).toFixed(2)} MXN</span></div>
            </div>
            <div className="border-t border-[#E5E3D8] pt-3 mt-3">
              <Button
                variant="outline"
                className="w-full border-[#E5E3D8] rounded-xl text-sm"
                onClick={async () => {
                  try {
                    const r = await axios.post(`${API}/orders/${id}/resend-email`, null, { params: { token } });
                    toast.success(`Email reenviado a ${r.data.email}. Revisa tu bandeja (y spam).`);
                  } catch {
                    toast.error('No se pudo reenviar el email');
                  }
                }}
                data-testid="resend-email-btn"
              >
                ✉️ ¿No te llegó el email? Reenviar
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OrderTrackPage;
